import { apiBaseUrl, sessionId } from './api'
import { readContext } from './context'

/**
 * Lo que el comensal hace en la carta, contado.
 *
 * ── Por qué existe ────────────────────────────────────────────────────────
 *
 * La carta no emitía NI UN evento. La tubería estaba entera —el esquema, el
 * endpoint, la cola del Service Worker, los rollups, la pantalla— y faltaba el
 * primer metro: nadie llamaba a la ingesta. Solo había datos de `qr_scan` y
 * `order_submit` porque esos los escribe el servidor, así que el embudo del
 * panel decía 16 → 11 → 0 → 0 → 33 y la portada prometía "sabes qué miran y
 * qué piden" sobre una pantalla que no podía saberlo.
 *
 * ── Cómo se manda ─────────────────────────────────────────────────────────
 *
 * Por lotes. Un comensal desliza decenas de platos en un minuto, y una
 * petición por gesto gastaría batería, datos y el límite de 60 peticiones por
 * minuto del endpoint. Se junta lo que pase en dos segundos —o veinte eventos,
 * lo que llegue antes— y se manda de una vez.
 *
 * Al ocultarse la pestaña se vacía con `sendBeacon`: es la única forma de que
 * el último lote sobreviva a cerrar la carta, que es exactamente lo que hace
 * todo el mundo después de pedir.
 *
 * ── Lo que NO puede hacer nunca ───────────────────────────────────────────
 *
 * Romper la carta. Todo va envuelto: si la analítica falla, se pierde un
 * evento y el comensal no se entera. Tampoco pesa: sin dependencias, porque la
 * carta tiene un presupuesto de bundle y cada kilobyte se nota en un 3G.
 */

type TipoEvento =
  | 'menu_view'
  | 'category_view'
  | 'product_view'
  | 'search'
  | 'add_to_cart'
  | 'remove_from_cart'
  | 'cart_open'
  | 'whatsapp_click'
  | 'waiter_show'

type Evento = {
  type: TipoEvento
  entity_type: 'category' | 'subcategory' | 'product' | null
  entity_id: string | null
  table_label: string | null
  props: Record<string, unknown> | null
  occurred_at: string
}

const ESPERA_MS = 2_000
const LOTE_MAXIMO = 20

let cola: Evento[] = []
let temporizador: ReturnType<typeof setTimeout> | null = null
let ultimaClave: string | null = null

/** Un uuid válido, o `null`: el contrato rechaza el lote entero por un id malo. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function track(
  type: TipoEvento,
  detalle: {
    entity_type?: Evento['entity_type']
    entity_id?: string | null
    props?: Record<string, unknown>
  } = {},
): void {
  try {
    const entityId = detalle.entity_id && UUID.test(detalle.entity_id) ? detalle.entity_id : null

    /*
     * El mismo evento dos veces seguidas se cuenta una.
     *
     * Es lo que pasa al volver a un plato con un gesto hacia atrás y otro hacia
     * delante en el mismo segundo, o cuando React monta un efecto dos veces en
     * desarrollo. Sin esto "vistas del plato" medía gestos, no interés.
     */
    const clave = `${type}:${entityId ?? ''}`
    if (clave === ultimaClave && type !== 'add_to_cart') return
    ultimaClave = clave

    cola.push({
      type,
      entity_type: entityId ? (detalle.entity_type ?? null) : null,
      entity_id: entityId,
      table_label: readContext().tableLabel,
      props: detalle.props ?? null,
      occurred_at: new Date().toISOString(),
    })

    if (cola.length >= LOTE_MAXIMO) vaciar()
    else if (!temporizador) temporizador = setTimeout(vaciar, ESPERA_MS)
  } catch {
    // La analítica nunca rompe la carta.
  }
}

function dispositivo(): 'mobile' | 'tablet' | 'desktop' {
  const ancho = window.innerWidth
  if (ancho < 700) return 'mobile'
  if (ancho < 1100) return 'tablet'
  return 'desktop'
}

/**
 * Manda lo acumulado.
 *
 * `usarBeacon` cuando la página se está yendo: `fetch` normal se cancela al
 * cerrar la pestaña, y el último lote —el del pedido— es el que más importa.
 */
function vaciar(usarBeacon = false): void {
  if (temporizador) {
    clearTimeout(temporizador)
    temporizador = null
  }
  if (cola.length === 0) return

  const { slug } = readContext()
  if (!slug) {
    cola = []
    return
  }

  const lote = cola.splice(0, 50)
  const cuerpo = JSON.stringify({
    session_id: sessionId(),
    device: dispositivo(),
    locale: navigator.language ?? null,
    theme: document.documentElement.dataset.theme ?? null,
    events: lote,
  })
  const url = `${apiBaseUrl}/menu/${encodeURIComponent(slug)}/events`

  try {
    /*
     * `sendBeacon` SOLO en el mismo origen.
     *
     * En producción la carta vive en `<local>.carta.cartazo.cl` y la API en
     * `console.cartazo.cl`. Un beacon viaja siempre con credenciales, y la API
     * pública responde `Access-Control-Allow-Origin: *`, que el navegador no
     * acepta junto a credenciales: el último lote —el que se manda al cerrar la
     * carta, después de pedir— se perdía sin error visible. En local funcionaba
     * porque Vite hace de proxy y todo es el mismo origen.
     *
     * `fetch` con `keepalive` sobrevive igual al cierre de la pestaña, hace su
     * preflight y no manda credenciales entre orígenes.
     */
    const mismoOrigen = new URL(url, window.location.href).origin === window.location.origin
    if (usarBeacon && mismoOrigen && typeof navigator.sendBeacon === 'function') {
      // `Blob` con tipo: sin él, sendBeacon manda `text/plain` y el endpoint no
      // lo lee como JSON.
      const enviado = navigator.sendBeacon(url, new Blob([cuerpo], { type: 'application/json' }))
      if (enviado) return
    }

    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: cuerpo,
      // Permite que la petición termine aunque la página se descargue.
      keepalive: true,
    }).catch(() => {
      // Sin red: el Service Worker lo encola y lo reintenta solo.
    })
  } catch {
    // Nunca rompe la carta.
  }

  if (cola.length > 0) vaciar(usarBeacon)
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') vaciar(true)
  })
  window.addEventListener('pagehide', () => vaciar(true))
}
